import {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import {
  UseComboboxState,
  UseComboboxStateChangeOptions,
  useCombobox,
} from "downshift";
import SearchIcon from "../icons/SearchIcon";
import useDebounce from "../../hooks/useDebounce";
import { AutoCompleteItem } from "./interface";
import AutoCompleteListItem from "./AutoCompleteListItem";
import { createPortal } from "react-dom";

export type AutoCompleteOnSelectedItemChange<T = void> = ({
  selectedItem,
  inputValue,
}: {
  selectedItem: AutoCompleteItem<T> | undefined | null;
  inputValue?: string;
}) => void;

export type AutoCompleteStateReducer<T> = (
  state: UseComboboxState<AutoCompleteItem<T>>,
  actionAndChanges: UseComboboxStateChangeOptions<AutoCompleteItem<T>>,
) => Partial<UseComboboxState<AutoCompleteItem<T>>>;

function AutoComplete<T>(props: {
  items: AutoCompleteItem<T>[];
  itemToString: (item: AutoCompleteItem<T> | null) => string;
  loading: boolean;
  onSelectedItemChange: AutoCompleteOnSelectedItemChange<T>;
  setInputValue: Dispatch<SetStateAction<string>>;
  clearAfterSelection?: boolean;
  placeholder?: string;
  className?: string;
  isInvalidClass?: string;
  sizeSmall?: boolean;
  sizeLarge?: boolean;
  includeFirstItem?: boolean;
  stateReducer?: AutoCompleteStateReducer<T>;
  onInputBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  focusInputOnMount?: boolean;
}) {
  const {
    items,
    itemToString,
    loading,
    onSelectedItemChange,
    setInputValue,
    clearAfterSelection,
    placeholder,
    className,
    isInvalidClass,
    sizeSmall,
    sizeLarge,
    includeFirstItem,
    stateReducer,
    onInputBlur,
  } = props;

  const firstID = "first";
  const addFirstItem =
    includeFirstItem &&
    !items.find((item) => item.id === firstID) &&
    items.length;

  if (addFirstItem) {
    items.unshift({
      id: firstID,
      header: `Vis alle resultater`,
      subheader: "Tryk Enter for at søge",
      icon: (
        <div className="c-search__result-item-icon">
          <SearchIcon height={16} />
        </div>
      ),
    });
  }

  // handlers
  function clearAfterSelectionHandler(
    _state: UseComboboxState<AutoCompleteItem<T>>,
    actionAndChanges: UseComboboxStateChangeOptions<AutoCompleteItem<T>>,
  ) {
    const { type, changes } = actionAndChanges;
    switch (type) {
      case useCombobox.stateChangeTypes.ItemClick:
        return {
          ...changes,
          inputValue: "",
        };
      case useCombobox.stateChangeTypes.InputKeyDownEnter:
        return {
          ...changes,
          inputValue: "",
        };
      default:
        return { ...changes };
    }
  }

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
  } = useCombobox({
    defaultHighlightedIndex: 0,
    items,
    itemToString,
    onSelectedItemChange: ({ selectedItem, inputValue }) => {
      onSelectedItemChange({
        selectedItem: selectedItem,
        inputValue: inputValue,
      });
    },
    stateReducer: stateReducer
      ? stateReducer
      : clearAfterSelection
        ? clearAfterSelectionHandler
        : (_state, actionAndChanges) => {
            return { ...actionAndChanges.changes };
          },
  });

  const debouncedInputValue = useDebounce(inputValue, 500);

  useEffect(() => {
    if (debouncedInputValue) setInputValue(debouncedInputValue);
    else {
      setInputValue("");
    }
  }, [debouncedInputValue, setInputValue]);

  const inputRef = useRef<HTMLInputElement>(null);

  const inputprops = getInputProps({
    id: "downshift-input",
    "aria-controls": "downshift-menu",
    "aria-labelledby": "downshift-label",
    onBlur: onInputBlur,
    ref: inputRef,
  });

  useLayoutEffect(() => {
    if (props.focusInputOnMount) inputRef?.current?.focus();
  }, [props.focusInputOnMount, inputRef]);
  return (
    <div
      className={`c-search${className ? ` ${className}` : ""}${
        sizeLarge ? " c-search--lg" : ""
      }`}
    >
      <input
        className={`c-search__input form-control${
          isInvalidClass ? ` ${isInvalidClass}` : ""
        }`}
        type={"search"}
        placeholder={placeholder ? placeholder : "Søg ..."}
        {...inputprops}
        value={inputprops.value ?? ""}
      />
      <span className="c-search__icon text-secondary">
        <SearchIcon />
      </span>

      {createPortal(
        <div
          {...getMenuProps({
            id: "downshift-menu",
            "aria-labelledby": "downshift-label",
          })}
          style={
            inputRef.current
              ? {
                  ...{
                    width: inputRef.current.offsetWidth,
                    position: "absolute",
                    top: inputRef.current.getClientRects()[0]?.bottom ?? "",
                    left: inputRef.current.getClientRects()[0]?.left ?? "",
                  },
                }
              : {}
          }
          className={`c-search__result${
            sizeSmall ? " c-search__result--sm" : ""
          }${sizeLarge ? " c-search__result--lg" : ""}`}
        >
          {isOpen &&
            inputRef.current &&
            items.map((item, index) => {
              return (
                <AutoCompleteListItem
                  key={item.id}
                  item={item}
                  index={index}
                  highlightedIndex={highlightedIndex}
                  getItemProps={getItemProps}
                />
              );
            })}
          {isOpen && loading && <div className="py-2 px-3">Søger ...</div>}
        </div>,
        document.getElementsByClassName("c-modal-overlay")[0] ?? document.body,
      )}
    </div>
  );
}
export default AutoComplete;
